// The nursing assessment form: complaints, structured history, allergies,
// pain, and the Braden/Morse instruments.
//
// Every save writes a COMPLETE new snapshot (ADR 0010), so the form opens
// pre-filled from the latest assessment and the nurse amends it. That makes
// "record the initial assessment", "reassess the patient" and "correct the
// history" the same action, each attributed and timestamped.
//
// Risk LEVELS are not entered here — they are derived from the instrument
// totals by the backend. The totals shown while typing are a local echo of the
// same arithmetic (clinicalScores.js); the stored values come back from the
// server on reload.
import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
  Grid, MenuItem, Stack, TextField, Typography,
} from "@mui/material";
import { toast } from "react-toastify";

import { NURSE_COLORS } from "../theme";
import { addAssessment } from "../api/nurseApi";
import {
  BRADEN_SUBSCALES, MORSE_ITEMS, HISTORY_SECTIONS,
  BRADEN_MIN, BRADEN_MAX, MORSE_MIN, MORSE_MAX,
  BRADEN_BANDS, MORSE_BANDS, PAIN_BANDS,
  scoreTotal, bradenLevel, morseLevel,
} from "../clinicalScores";
import ScoreBar from "./ScoreBar";
import { formatIstDateTime } from "../../../utils/istDateTime";

const LEVEL_COLOR = { Low: "success", Moderate: "warning", High: "error" };

// Pull the answers for one instrument out of a stored assessment, dropping the
// server's computed `total` — it is recomputed, never edited.
function answersOf(stored) {
  const { total, ...answers } = stored || {};
  return answers;
}

function InstrumentSection({ title, definition, answers, onChange, disabled }) {
  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle2">{title}</Typography>
      <Grid container spacing={1.5}>
        {definition.map((item) => (
          <Grid item xs={12} sm={6} key={item.key}>
            <TextField
              select fullWidth size="small"
              label={item.label}
              helperText={item.help}
              disabled={disabled}
              value={answers[item.key] ?? ""}
              onChange={(e) => onChange(item.key, e.target.value === "" ? undefined : Number(e.target.value))}
            >
              <MenuItem value="">— Not answered —</MenuItem>
              {item.options.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label} ({opt.value})
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        ))}
      </Grid>
    </Stack>
  );
}

export default function NursingAssessmentDialog({
  open, onClose, onSaved, admissionId, patientId, latest,
}) {
  const [form, setForm] = useState({ chief_complaints: "", allergies: "", pain_score: "" });
  const [history, setHistory] = useState({});
  const [braden, setBraden] = useState({});
  const [morse, setMorse] = useState({});
  const [saving, setSaving] = useState(false);

  // Carry forward: re-seed from the latest snapshot each time the dialog opens,
  // so a cancelled edit never leaks into the next one.
  useEffect(() => {
    if (!open) return;
    setForm({
      chief_complaints: latest?.chief_complaints || "",
      allergies: (latest?.allergies || []).join(", "),
      pain_score: latest?.pain_score ?? "",
    });
    setHistory(latest?.history || {});
    setBraden(answersOf(latest?.braden));
    setMorse(answersOf(latest?.morse));
  }, [open, latest]);

  const bradenTotal = useMemo(() => scoreTotal(braden, BRADEN_SUBSCALES), [braden]);
  const morseTotal = useMemo(() => scoreTotal(morse, MORSE_ITEMS), [morse]);
  const ulcerRisk = bradenLevel(bradenTotal);
  const fallRisk = morseLevel(morseTotal);

  const setAnswer = (setter) => (key, value) =>
    setter((prev) => {
      const next = { ...prev };
      if (value === undefined) delete next[key];
      else next[key] = value;
      return next;
    });

  // An instrument must be fully answered or fully blank — the backend rejects
  // a partial one, so catch it here rather than on the round trip.
  const partial = (answers, definition) => {
    const count = definition.filter((d) => answers[d.key] != null).length;
    return count > 0 && count < definition.length;
  };
  const bradenPartial = partial(braden, BRADEN_SUBSCALES);
  const morsePartial = partial(morse, MORSE_ITEMS);

  const painNum = form.pain_score === "" ? null : Number(form.pain_score);
  const painInvalid = painNum != null && (Number.isNaN(painNum) || painNum < 0 || painNum > 10);

  const blocked = bradenPartial || morsePartial || painInvalid || saving;

  const save = async () => {
    setSaving(true);
    try {
      const res = await addAssessment({
        admission_id: admissionId,
        patient_id: patientId,
        chief_complaints: form.chief_complaints.trim() || null,
        history: HISTORY_SECTIONS.reduce((acc, s) => {
          const text = (history[s.key] || "").trim();
          if (text) acc[s.key] = text;
          return acc;
        }, {}),
        allergies: form.allergies.split(",").map((s) => s.trim()).filter(Boolean),
        pain_score: painNum,
        // Send only fully answered instruments; {} means "not scored".
        braden: bradenTotal == null ? null : braden,
        morse: morseTotal == null ? null : morse,
      });
      // Flat envelope — a 200 can still carry a rejection (e.g. a subscale the
      // backend revalidated), so check status rather than assuming success.
      if (res?.status !== "Success") {
        toast.error(res?.message || "Could not save the assessment");
        return;
      }
      toast.success("Assessment recorded");
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error("Could not save the assessment");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ fontWeight: 700, color: NURSE_COLORS.primary }}>
        Nursing Assessment
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ mt: 0.5 }}>
          {latest?.assessed_at && (
            <Typography variant="caption" color="text.secondary">
              Carried forward from the assessment of{" "}
              {formatIstDateTime(latest.assessed_at)}
              {latest.assessed_by_name ? ` by ${latest.assessed_by_name}` : ""}. Saving files a new one.
            </Typography>
          )}

          <TextField
            label="Chief complaints" multiline rows={2} fullWidth size="small"
            value={form.chief_complaints}
            onChange={(e) => setForm({ ...form, chief_complaints: e.target.value })}
            placeholder="What the patient reports, in their own words where possible"
          />

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>History</Typography>
            <Grid container spacing={1.5}>
              {HISTORY_SECTIONS.map((section) => (
                <Grid item xs={12} sm={6} key={section.key}>
                  <TextField
                    label={section.label} multiline rows={2} fullWidth size="small"
                    value={history[section.key] || ""}
                    onChange={(e) => setHistory({ ...history, [section.key]: e.target.value })}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>

          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Pain score (0-10)" type="number" fullWidth size="small"
                inputProps={{ min: 0, max: 10 }}
                error={painInvalid}
                helperText={painInvalid ? "Must be 0-10" : " "}
                value={form.pain_score}
                onChange={(e) => setForm({ ...form, pain_score: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                label="Allergies (comma-separated)" fullWidth size="small"
                helperText=" "
                value={form.allergies}
                onChange={(e) => setForm({ ...form, allergies: e.target.value })}
              />
            </Grid>
          </Grid>

          <Divider />

          <InstrumentSection
            title="Braden Scale — pressure ulcer risk"
            definition={BRADEN_SUBSCALES}
            answers={braden}
            onChange={setAnswer(setBraden)}
            disabled={saving}
          />
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <Chip
              size="small"
              color={ulcerRisk ? LEVEL_COLOR[ulcerRisk] : "default"}
              label={bradenTotal == null ? "Braden: not scored" : `Braden ${bradenTotal} · ${ulcerRisk}`}
            />
            {bradenPartial && (
              <Typography variant="caption" color="error">
                Answer every subscale, or clear them all — a partial Braden is not a score.
              </Typography>
            )}
          </Stack>

          <Divider />

          <InstrumentSection
            title="Morse Fall Scale — fall risk"
            definition={MORSE_ITEMS}
            answers={morse}
            onChange={setAnswer(setMorse)}
            disabled={saving}
          />
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <Chip
              size="small"
              color={fallRisk ? LEVEL_COLOR[fallRisk] : "default"}
              label={morseTotal == null ? "Morse: not scored" : `Morse ${morseTotal} · ${fallRisk}`}
            />
            {morsePartial && (
              <Typography variant="caption" color="error">
                Answer every item, or clear them all — a partial Morse is not a score.
              </Typography>
            )}
          </Stack>

          {(bradenTotal != null || morseTotal != null || painNum != null) && (
            <>
              <Divider />
              <Stack spacing={1.5}>
                <ScoreBar
                  label="Braden (lower = higher risk)" score={bradenTotal}
                  min={BRADEN_MIN} max={BRADEN_MAX} bands={BRADEN_BANDS} level={ulcerRisk}
                />
                <ScoreBar
                  label="Morse (higher = higher risk)" score={morseTotal}
                  min={MORSE_MIN} max={MORSE_MAX} bands={MORSE_BANDS} level={fallRisk}
                />
                <ScoreBar
                  label="Pain" score={painInvalid ? null : painNum}
                  min={0} max={10} bands={PAIN_BANDS}
                  level={PAIN_BANDS.find((b) => (painNum ?? 99) <= b.upTo)?.level}
                />
              </Stack>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={blocked}>
          {saving ? "Saving…" : "Save Assessment"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
